import * as Sentry from "@sentry/react";
import { useState, useEffect } from "react";
import Turnstile, { useTurnstile } from "react-turnstile";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import Modal from "react-modal";
import Auth, { getApiResponseStatus, isExpectedApiResponseError } from "../../Auth";
import getCastle from "../../CastleConfiguration";

Modal.setAppElement("#root");

const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(55, 55, 55, 0.7)",
    zIndex: "100000001",
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    zIndex: "20000",
    transform: "translate(-50%, -50%)",
    width: "400px",
    borderRadius: "15px",
    border: "0px",
    padding: "30px",
    background: "var(--menu-color)",
    boxShadow: "var(--default-drop-shadow)",
    textAlign: "left",
    fontFamily: "Inter",
  },
};

const getTurnstileErrorReason = (error) => {
  if (!error) return "unknown";
  if (typeof error === "string") return error;
  return error.message || error.code || "unknown";
};

function TurnstileHost() {
  const turnstile = useTurnstile();
  const subscription = useSudowriteStore((state) => state.subscription);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (subscription?.requiresTurnstile) {
      setIsOpen(true);
    }
  }, [subscription]);

  if (!subscription) return null;
  if (!subscription.requiresTurnstile) {
    return null;
  }

  const getCastleTokenOrSwallowError = async () => {
    try {
      return await getCastle().createRequestToken();
    } catch (err) {
      Sentry.captureMessage(`Castle token error on turnstile: ${err?.message || "unknown"}`, { tags: { type: "castle_token_error_on_turnstile" } });
      return null;
    }
  };

  const resetTurnstile = () => {
    try {
      turnstile?.reset?.();
    } catch {
      // Reset can race with the challenge container being removed.
    }
  };

  const handleVerify = async (token) => {
    try {
      const castleToken = await getCastleTokenOrSwallowError();
      const response = await Auth.fetchWithIdToken("/api/cloudflare/verify", { turnstileToken: token, castleToken });
      if (!response?.success) {
        resetTurnstile();
      } else {
        setIsOpen(false);
        Sentry.addBreadcrumb({
          category: "turnstile",
          message: "TurnstileHost resolved successfully",
          data: { subscriptionId: subscription.id },
        });
      }
    } catch (err) {
      // Cloudflare verification returns 400 when the challenge is not accepted;
      // that is an expected user-flow state, not a frontend exception.
      if (isExpectedApiResponseError(err) || getApiResponseStatus(err) === 400) {
        resetTurnstile();
        return;
      }
      Sentry.captureException(err, { tags: { type: "turnstile_verification_error" } });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      style={modalStyles}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      <h2 style={{ marginTop: 0, marginBottom: "20px" }}>Are you human?</h2>
      <p style={{ marginBottom: "30px", color: "var(--grey-1)" }}>You never know these days.</p>
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <Turnstile
          sitekey="0x4AAAAAAA5V8MR88ICOJZ6k"
          onVerify={handleVerify}
          refreshExpired="auto"
          onLoad={async () => {
            Sentry.addBreadcrumb({
              category: "turnstile",
              message: "TurnstileHost opened",
              data: { subscriptionId: subscription.id },
            });
            await Auth.fetchWithIdToken("/api/cloudflare/markAsShown");
          }}
          onError={(error) => {
            const reason = getTurnstileErrorReason(error);
            Sentry.addBreadcrumb({
              category: "turnstile",
              message: "Turnstile widget error",
              data: { reason },
            });
            resetTurnstile();
          }}
          onTimeout={() => {
            Sentry.addBreadcrumb({ category: "turnstile", message: "TurnstileHost timeout" });
            resetTurnstile();
          }}
          onUnsupported={() => {
            Sentry.captureMessage("TurnstileHost unsupported", { tags: { type: "turnstile_unsupported" }, level: "warning" });
          }}
          onExpired={() => {
            Sentry.addBreadcrumb({ category: "turnstile", message: "TurnstileHost expired" });
            resetTurnstile();
          }}
        />
      </div>
    </Modal>
  );
}

export default TurnstileHost;
